import * as XLSX from 'xlsx';
import { getPresetDates } from './SalesReportFilters';

export const PRODUCT_SALES_METRICS = [
  { key: 'sold_quantity', label: 'SL bán', quantity: true },
  { key: 'sale_revenue', label: 'Doanh thu' },
  { key: 'return_quantity', label: 'SL trả', quantity: true },
  { key: 'return_amount', label: 'Giá trị trả' },
  { key: 'net_revenue', label: 'Doanh thu thuần' },
];

export const getProductSalesQuery = (search = '') => {
  const params = new URLSearchParams(search);
  const dates = getPresetDates('30-days');
  return {
    date_from: params.get('date_from') || dates.date_from,
    date_to: params.get('date_to') || dates.date_to,
    business_id: params.get('business_id') || '',
    store_id: params.get('store_id') || '',
    salesperson: params.get('salesperson') || '',
    keyword: params.get('keyword') || '',
    price_list_id: params.get('price_list_id') || '',
    page: Math.max(1, Number(params.get('page')) || 1),
    items_per_page: Math.max(1, Number(params.get('items_per_page')) || 20),
  };
};

export const metricValue = (row, key) => row?.[key + '_exact'] ?? row?.[key];

export const formatProductSalesNumber = (value, maximumFractionDigits = 6) => {
  if (value === undefined || value === null || value === '') return '—';
  const match = String(value).trim().match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!match) {
    const number = Number(value);
    return Number.isFinite(number)
      ? new Intl.NumberFormat('vi-VN', { maximumFractionDigits }).format(number)
      : '—';
  }
  const integer = match[2].replace(/^0+(?=\d)/, '');
  const fraction = (match[3] || '').replace(/0+$/, '');
  const negative = match[1] === '-' && (integer !== '0' || fraction);
  return (negative ? '-' : '') + integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.') +
    (fraction ? ',' + fraction : '');
};

export const formatProductSalesMetric = (row, metric) => {
  const value = formatProductSalesNumber(metricValue(row, metric.key), metric.quantity ? 6 : 4);
  return value === '—' || metric.quantity ? value : value + ' đ';
};

export const getProductSalesDiagnostics = (meta = {}) => {
  const diagnostics = [];
  const dateCount = Number(meta.unresolved_event_document_count);
  if (dateCount > 0) {
    const count = formatProductSalesNumber(dateCount, 0);
    const amount = formatProductSalesNumber(meta.unresolved_event_revenue_exact, 4);
    const description = meta.source === 'ACTUAL_CUSTOMER_FULFILMENT'
      ? count + ' chứng từ hạch toán chưa đủ ngày ghi nhận, giá trị ' + amount +
        ' đ chưa đủ thông tin để đối chiếu với doanh thu thực xuất của báo cáo.'
      : count + ' chứng từ chưa đủ ngày ghi nhận, giá trị ' + amount + ' đ chưa tính vào báo cáo.';
    diagnostics.push(description + (meta.unresolved_event_date_basis === 'ACCOUNTING_DATE'
      ? ' Phạm vi đối chiếu theo ngày hạch toán trong kỳ đã chọn.' : ''));
  }
  const productCount = Number(meta.unresolved_product_event_count);
  if (productCount > 0) diagnostics.push(
    formatProductSalesNumber(productCount, 0) + ' nghiệp vụ chưa đủ chi tiết hàng hóa, giá trị ' +
    formatProductSalesNumber(meta.unresolved_product_revenue_exact, 4) + ' đ đang được giữ tại dòng Chưa xác định hàng hóa để đối chiếu tổng báo cáo.',
  );
  return diagnostics;
};

export const employeeLabel = (row = {}) =>
  row.username === '__UNASSIGNED__' ? row.full_name || 'Chưa xác định nhân viên'
    : [row.username, row.full_name].filter(Boolean).join(' - ');

const excelValue = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const canonical = String(value);
  const significantDigits = canonical.replace(/^[+-]?0*/, '').replace('.', '').replace(/0+$/, '');
  return significantDigits.length <= 15 && Number.isFinite(Number(value)) ? Number(value) : canonical;
};

const metricCells = (row) => PRODUCT_SALES_METRICS.map((metric) => excelValue(metricValue(row, metric.key)));
const metricHeaders = PRODUCT_SALES_METRICS.map((metric) => metric.label);
const appendSheet = (workbook, rows, name, widths) => {
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet['!cols'] = widths.map((wch) => ({ wch }));
  XLSX.utils.book_append_sheet(workbook, sheet, name);
};

export const buildProductSalesWorkbook = (report, { employee = false, query = {} } = {}) => {
  const workbook = XLSX.utils.book_new();
  const items = report?.items || [];
  const context = [
    ['Báo cáo', employee ? 'Bán hàng theo nhân viên và hàng hóa' : 'Bán hàng theo hàng hóa'],
    ['Từ ngày', query.date_from || '', 'Đến ngày', query.date_to || ''],
    ['Chi nhánh', query.business_id || 'Tất cả được phân quyền', 'Cửa hàng', query.store_id || 'Tất cả được phân quyền'],
    ['Nhân viên', query.salesperson || 'Tất cả được phân quyền', 'Tìm kiếm', query.keyword || ''],
    ['Phạm vi', 'Bán khách (bán lẻ và bán sỉ); doanh thu trước VAT'],
    ...getProductSalesDiagnostics(report?.meta).map((diagnostic) => ['Đối chiếu số liệu', diagnostic]),
    ...(report?.meta?.notices || []).map((notice) => ['Ghi chú', String(notice)]),
  ];
  appendSheet(workbook, context, 'Phạm vi báo cáo', [28, 90, 20, 30]);
  if (employee) {
    appendSheet(workbook, [
      ['Mã NV', 'Tên nhân viên', ...metricHeaders],
      ...items.map((row) => [row.username, row.full_name, ...metricCells(row)]),
      ['Tổng cộng', '', ...metricCells(report.summary || {})],
    ], 'Tổng hợp nhân viên', [18, 35, 18, 24, 18, 24, 24]);
  }
  const productRows = employee
    ? items.flatMap((person) => (person.products || []).map((product) => ({ person, product })))
    : items.map((product) => ({ product }));
  const headers = [...(employee ? ['Mã NV', 'Tên nhân viên'] : []), 'Mã hàng', 'Tên hàng', 'ĐVT', ...metricHeaders];
  appendSheet(workbook, [
    headers,
    ...productRows.map(({ person, product }) => [
      ...(employee ? [person.username, person.full_name] : []),
      product.product_code, product.product_name, product.unit_name || '', ...metricCells(product),
    ]),
    ...(employee ? [] : [['Tổng cộng', '', '', ...metricCells(report.summary || {})]]),
  ], 'Chi tiết hàng hóa', [...(employee ? [18, 35] : []), 20, 48, 12, 18, 24, 18, 24, 24]);
  return workbook;
};
